import {
  CalendarDays,
  ChartLine,
  ChevronLeft,
  ChevronRight,
  Clock,
  PlayCircle,
  Video,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useEntitlements, useMe } from '@/auth/queries';
import { Avatar } from '@/components/Avatar';
import { BotonGrande } from '@/components/BloquesConsalud';
import { useStagger } from '@/lib/motion';
import { AVATARES } from '@/lib/avatares';
import { tratamiento } from '@/lib/utils';

interface Tarjeta {
  titulo: string;
  imagen: string;
  items: { etiqueta: string; ruta: string; permiso?: string }[];
}

/** Las tres "tarjetas carpeta" del inicio legacy (skill §3), mismo orden y copy. */
function tarjetas(anio: number): Tarjeta[] {
  return [
    {
      titulo: `ENARM ${anio}`,
      imagen: '/img/inicio/enarm.jpg',
      items: [
        { etiqueta: 'Clases', ruta: '/curso-enarm/clases' },
        { etiqueta: 'Talleres', ruta: '/curso-enarm/talleres', permiso: 'workshop.access' },
        { etiqueta: 'Exámenes', ruta: '/curso-enarm/examenes-prime', permiso: 'exam.access' },
        {
          etiqueta: 'Inglés médico – Key points',
          ruta: '/manuales/ingles-medico',
          permiso: 'manual.medical_english.access',
        },
        {
          etiqueta: 'Mnemotecnias – Smart words',
          ruta: '/manuales/mnemotecnias',
          permiso: 'manual.mnemonics.access',
        },
        {
          etiqueta: 'Clasificaciones y tablas',
          ruta: '/manuales/clasificaciones-y-tablas',
          permiso: 'manual.tables.access',
        },
      ],
    },
    {
      titulo: 'Manuales ENARM',
      imagen: '/img/inicio/manuales.jpg',
      items: [
        {
          etiqueta: 'Ultra resúmenes',
          ruta: '/manuales/ultra-resumenes',
          permiso: 'manual.ultra_summaries.access',
        },
        {
          etiqueta: 'Resúmenes GPC',
          ruta: '/curso-enarm/biblioteca/resumenes-gpc',
          permiso: 'library.gpc_summaries.access',
        },
        {
          etiqueta: 'Algoritmos',
          ruta: '/manuales/algoritmos',
          permiso: 'manual.algorithms.access',
        },
        {
          etiqueta: 'Manual ENARM Plus',
          ruta: '/manuales/enarm-plus',
          permiso: 'manual.enarm_plus.access',
        },
      ],
    },
    {
      titulo: 'Biblioteca médica',
      imagen: '/img/inicio/biblioteca.jpg',
      items: [
        {
          etiqueta: 'Presentaciones',
          ruta: '/curso-enarm/biblioteca/presentaciones',
          permiso: 'library.presentations.access',
        },
        {
          etiqueta: 'Guías GPC',
          ruta: '/curso-enarm/biblioteca/guias-gpc',
          permiso: 'class.gpc_guides.access',
        },
        {
          etiqueta: 'Flashcards',
          ruta: '/curso-enarm/biblioteca/flashcards',
          permiso: 'library.flashcards.access',
        },
        {
          etiqueta: 'Evaluadas en el ENARM',
          ruta: '/curso-enarm/biblioteca/flashcards-evaluadas',
          permiso: 'library.flashcards.access',
        },
        {
          etiqueta: 'Video tips',
          ruta: '/curso-enarm/biblioteca/video-tips',
          permiso: 'library.videos.access',
        },
      ],
    },
  ];
}

interface Diapositiva {
  rotulo: string;
  titulo: string;
  texto: string;
  imagen: string;
  cta: { etiqueta: string; ruta: string };
}

const DIAPOSITIVAS: Diapositiva[] = [
  {
    rotulo: '- Programas presenciales y Online -',
    titulo: 'Acredita el ENARM, ¡A la primera!',
    texto: 'Somos el mejor sistema de enseñanza ENARM en México y América Latina',
    imagen: '/img/mascotas/doctor-foto.png',
    cta: { etiqueta: 'Ver licencias', ruta: '/licencias' },
  },
  {
    rotulo: '- Simulador ENARM -',
    titulo: 'Practica como el día del examen',
    texto: 'Casos clínicos cronometrados con resultados por especialidad',
    imagen: '/img/inicio/simulador.webp',
    cta: { etiqueta: 'Ir al simulador', ruta: '/simulador' },
  },
];

const REDES: { nombre: string; url: string; icono: ReactNode }[] = [
  {
    nombre: 'Facebook',
    url: 'https://www.facebook.com/Consalud.enarm',
    icono: (
      <path d="M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9H17l.5-4h-4V8.9c0-.6.3-.9.5-.9Z" />
    ),
  },
  {
    nombre: 'X (Twitter)',
    url: 'https://twitter.com/consaludenarm',
    icono: (
      <path d="M17.8 3h3.1l-6.8 7.8L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.3-8.3L2 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5Z" />
    ),
  },
  {
    nombre: 'Instagram',
    url: 'https://www.instagram.com/consaludenarm/',
    icono: (
      <path d="M12 7.3a4.7 4.7 0 1 0 0 9.4 4.7 4.7 0 0 0 0-9.4Zm0 7.7a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm6-7.9a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0ZM21 8c-.1-1.5-.4-2.8-1.5-3.9S17.5 2.6 16 2.6C14.4 2.5 9.6 2.5 8 2.6c-1.5.1-2.8.4-3.9 1.5S2.6 6.5 2.6 8c-.1 1.6-.1 6.4 0 8 .1 1.5.4 2.8 1.5 3.9s2.4 1.4 3.9 1.5c1.6.1 6.4.1 8 0 1.5-.1 2.8-.4 3.9-1.5s1.4-2.4 1.5-3.9c.1-1.6.1-6.4 0-8Zm-2 9.9a3.2 3.2 0 0 1-1.8 1.8c-1.3.5-4.2.4-5.2.4s-4 .1-5.2-.4A3.2 3.2 0 0 1 5 17.9c-.5-1.3-.4-4.2-.4-5.9s-.1-4.6.4-5.9A3.2 3.2 0 0 1 6.8 4.3C8 3.8 11 3.9 12 3.9s4-.1 5.2.4A3.2 3.2 0 0 1 19 6.1c.5 1.3.4 4.2.4 5.9s.1 4.6-.4 5.9Z" />
    ),
  },
  {
    nombre: 'YouTube',
    url: 'https://www.youtube.com/channel/UCbvrLtn5dhpBDdJl2w5QNAg',
    icono: (
      <path d="M22.5 7.2a2.8 2.8 0 0 0-2-2C18.8 4.8 12 4.8 12 4.8s-6.8 0-8.5.4a2.8 2.8 0 0 0-2 2C1.1 8.9 1.1 12 1.1 12s0 3.1.4 4.8a2.8 2.8 0 0 0 2 2c1.7.4 8.5.4 8.5.4s6.8 0 8.5-.4a2.8 2.8 0 0 0 2-2c.4-1.7.4-4.8.4-4.8s0-3.1-.4-4.8ZM9.8 15.1V8.9l5.7 3.1-5.7 3.1Z" />
    ),
  },
];

/** Banner promocional vertical de la columna izquierda (carrusel del inicio legacy). */
function BannerPromo() {
  const [i, setI] = useState(0);
  const total = DIAPOSITIVAS.length;
  const mover = (d: number) => setI((n) => (n + d + total) % total);
  useEffect(() => {
    const id = window.setInterval(() => setI((n) => (n + 1) % total), 7000);
    return () => window.clearInterval(id);
  }, [i, total]);
  const d = DIAPOSITIVAS[i] ?? DIAPOSITIVAS[0]!;

  return (
    <section
      data-card
      aria-roledescription="carrusel"
      className="relative flex min-h-[30rem] flex-col items-center overflow-hidden rounded-[var(--radius-card)] bg-portada px-6 pt-6 text-center text-white shadow-[var(--shadow-vidrio)]"
    >
      <img src="/img/isotipo-cerebro-blanco.png" alt="" className="h-12 w-auto" />
      <p className="mt-1 text-sm font-extrabold tracking-wide uppercase">Consalud</p>
      <div key={i} className="animate__animated animate__fadeIn flex flex-1 flex-col items-center">
        <p className="mt-3 text-sm font-bold">{d.rotulo}</p>
        <h2 className="mt-1 text-xl leading-tight font-extrabold">{d.titulo}</h2>
        <p className="mt-2 text-sm text-white/90">{d.texto}</p>
        <Link
          to={d.cta.ruta}
          className="btn-cs relative z-10 mt-4 border-2 border-white text-white uppercase hover:bg-white/15"
        >
          {d.cta.etiqueta}
        </Link>
        <img src={d.imagen} alt="" className="mt-auto max-h-60 w-auto object-contain pt-4" />
      </div>
      <button
        type="button"
        onClick={() => mover(-1)}
        aria-label="Anterior"
        className="absolute top-1/2 left-1 z-10 rounded-full p-1 text-white/80 hover:bg-white/15 hover:text-white"
      >
        <ChevronLeft size={26} />
      </button>
      <button
        type="button"
        onClick={() => mover(1)}
        aria-label="Siguiente"
        className="absolute top-1/2 right-1 z-10 rounded-full p-1 text-white/80 hover:bg-white/15 hover:text-white"
      >
        <ChevronRight size={26} />
      </button>
    </section>
  );
}

export function InicioPage() {
  const { data: me } = useMe();
  const { data: ent } = useEntitlements();
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-card]', { step: 80, deps: [ent?.enarmYear] });

  const anio = ent?.enarmYear ?? new Date().getFullYear();
  const tiene = (p?: string) => !p || !!ent?.permissions.includes(p);
  const sinLicencia = ent !== undefined && ent.memberships.length === 0;
  const bienvenida =
    me?.gender === 'H'
      ? '¡Bienvenido'
      : me?.gender === 'M'
        ? '¡Bienvenida'
        : '¡Te damos la bienvenida';

  return (
    <div ref={root} className="space-y-6">
      <header data-card>
        <h1 className="titulo-seccion">
          {bienvenida}
          {me ? `, ${tratamiento(me.name, me.gender)}` : ''}!
        </h1>
        <p className="mt-2 text-texto-2">Tu preparación para el ENARM {anio}, en un solo lugar.</p>
      </header>

      {sinLicencia && (
        <section
          data-card
          className="card-vidrio flex flex-col gap-4 p-5 sm:flex-row sm:items-center"
        >
          <div className="flex-1">
            <p className="font-extrabold text-texto">Tu cuenta aún no tiene una licencia activa</p>
            <p className="mt-1 text-sm text-texto-2">
              Elige la licencia que mejor se adapte a tu preparación para desbloquear clases,
              simulador y manuales.
            </p>
          </div>
          <Link to="/licencias" className="btn-cs btn-accion pulse-cta">
            Ver licencias
          </Link>
        </section>
      )}

      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,3fr)_minmax(0,4fr)_minmax(0,3fr)]">
        <div className="hidden md:block">
          <BannerPromo />
        </div>

        <div className="flex flex-col gap-6">
          {tarjetas(anio).map((t) => (
            <article
              key={t.titulo}
              data-card
              className="card-vidrio card-vidrio-hover flex gap-4 overflow-hidden"
            >
              <img
                src={t.imagen}
                alt=""
                loading="lazy"
                className="w-28 shrink-0 self-stretch object-cover sm:w-36"
              />
              <div className="min-w-0 flex-1 py-4 pr-4">
                <h2 className="border-b-2 border-verde-500 pb-1 text-xl font-semibold text-azul-texto uppercase">
                  {t.titulo}
                </h2>
                <ul className="mt-2">
                  {t.items.map((i) => (
                    <li key={i.etiqueta}>
                      <Link
                        to={i.ruta}
                        className={`flex items-center justify-between gap-2 rounded px-1 py-0.5 text-[0.8rem] tracking-wide text-texto-2 uppercase hover:bg-[var(--vidrio-fuerte)] hover:text-texto ${tiene(i.permiso) ? '' : 'opacity-60'}`}
                      >
                        {i.etiqueta}
                        {!tiene(i.permiso) && (
                          <span className="insignia insignia-exclusivo normal-case">Exclusivo</span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <aside data-card className="flex flex-col items-center gap-3 md:col-span-2 xl:col-span-1">
          <Link to="/curso-enarm/temario/organizar" className="btn-cs btn-primario rounded-md">
            <Clock size={16} /> ¿Cómo organizo mi estudio?
          </Link>
          <div className="flex gap-3">
            <Link to="/curso-enarm/temario" className="btn-cs btn-exito rounded-md">
              <CalendarDays size={16} /> Temario
            </Link>
            <Link
              to="/avances"
              className="btn-cs rounded-md bg-azul-400 text-white hover:brightness-110"
            >
              <ChartLine size={16} /> Mi avance
            </Link>
          </div>
          {/* Encuadre de medio cuerpo, como la ilustración original: se recortan las piernas. */}
          <div
            role="img"
            aria-label="Doctora y doctor de Consalud"
            className="my-2 flex h-48 w-max shrink-0 items-start overflow-hidden px-4 pt-2"
          >
            <Avatar src={AVATARES.doctoraSenala} className="h-[270px] shrink-0" />
            <Avatar src={AVATARES.doctorIzq} className="-ml-3 h-[270px] shrink-0" />
          </div>
          <div className="flex w-full max-w-xs flex-col gap-3">
            {ent?.permissions.includes('live.history.access') && (
              <BotonGrande to="/live/grabaciones" icono={<Video size={22} />}>
                Clases grabadas
              </BotonGrande>
            )}
            <BotonGrande to="/simulador" icono={<PlayCircle size={22} />}>
              Simulador ENARM
            </BotonGrande>
          </div>
          <p className="mt-4 font-bold text-texto">Síguenos en nuestras redes:</p>
          <ul className="flex gap-3">
            {REDES.map((r) => (
              <li key={r.nombre}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={r.nombre}
                  className="grid h-9 w-9 place-items-center rounded-full bg-azul-600 text-white transition-transform hover:-translate-y-0.5"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                    {r.icono}
                  </svg>
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
